/**
 * Promote (T387 M6): evidence → a provisional standing rate. Opens PRE-FILLED
 * from the queue card — place, service, and the last agreed price per QUOTED
 * band; unquoted bands stay honestly empty. One required note; "Save as
 * provisional" → POST /api/promote (which also resolves the source card in
 * the same transaction). A duplicate 409s into edit-instead.
 *
 * Q36 (soft, client-side, NEVER a server refusal): a band price over the
 * new-entry sanity ceiling warns once; a second Save proceeds; the warning
 * re-arms when any amount is edited.
 */
import { useState } from 'preact/hooks';
import { promoteEntry } from '../../lib/actions.js';
import { bands, type Registry } from '../../store/registry.js';
import { normalizePlaceName } from '../../lib/format.js';
import { Q36_NEW_ENTRY_CEILING_CENTS } from '../../lib/q36.js';
import { savedMessage } from './AddEntryForm.js';
import type { PromoteSeed } from './QueueView.js';

export type PromoteFormProps = {
  registry: Registry;
  seed: PromoteSeed;
  /** ok + notice; on 409 `name` carries the duplicate for edit-instead. */
  onDone: (ok: boolean, message: string, name?: string) => void;
  onCancel: () => void;
};

export function PromoteForm({ registry, seed, onDone, onCancel }: PromoteFormProps) {
  // The queue card shows the VERBATIM evidence; the price book gets clean copy
  // by default ("HOWTH," → "Howth" — ux finding). Still editable before save.
  const [name, setName] = useState(normalizePlaceName(seed.name));
  // Route promotes (golf) need their hub — evidence never carries one, the
  // operator supplies it (M6 gate defect 2: without this every golf promote
  // 400'd with no fixable field on screen).
  const [hubName, setHubName] = useState('');
  const [prices, setPrices] = useState<Record<string, string>>(() => {
    const out: Record<string, string> = {};
    for (const [band, cents] of Object.entries(seed.bandCents)) {
      out[band] = String(cents / 100);
    }
    return out;
  });
  const [note, setNote] = useState('');
  const [amountWarned, setAmountWarned] = useState(false);

  const submit = async () => {
    if (!name.trim()) {
      onDone(false, 'Enter the name.');
      return;
    }
    if (seed.kind === 'route' && !hubName.trim()) {
      onDone(false, 'Enter the hub the route starts from.');
      return;
    }
    const bandPrices: Record<string, number> = {};
    for (const band of bands(registry)) {
      const raw = (prices[band] ?? '').trim();
      if (raw === '') continue;
      const euros = Number(raw);
      if (!Number.isFinite(euros) || euros <= 0) {
        onDone(false, `Enter a price in euro for band ${band}, or leave it blank.`);
        return;
      }
      bandPrices[band] = Math.round(euros * 100);
    }
    if (Object.keys(bandPrices).length === 0) {
      onDone(false, 'Enter at least one band price.');
      return;
    }
    if (!note.trim()) {
      onDone(false, 'A note is required when adding a price.');
      return;
    }
    // Q36 warn-once: an implausibly high standing price is almost always a
    // typo. Soft — save again to confirm.
    if (!amountWarned && Object.values(bandPrices).some((c) => c > Q36_NEW_ENTRY_CEILING_CENTS)) {
      setAmountWarned(true);
      onDone(false, 'That looks very high for a standing price. Check the amounts, then save again to confirm.');
      return;
    }
    const res = await promoteEntry({
      // No cast: PromoteSeed.kind is already PriceEntryKind (QueueView.ts).
      kind: seed.kind,
      service: seed.service,
      name: name.trim(),
      ...(seed.kind === 'route' ? { hubName: hubName.trim() } : {}),
      bandPrices,
      note: note.trim(),
      ...(seed.sourceBookingId != null ? { sourceBookingId: seed.sourceBookingId } : {}),
      ...(seed.source != null ? { source: seed.source } : {}),
    });
    if (res.ok) {
      // T608 M8b — the SAME composer as AddEntryForm, imported rather than
      // re-typed: this form carried a byte-identical copy of the false
      // "a colleague confirms" sentence, and the promote path is the WORSE half
      // of the silent-failure defect. It does write a locations row
      // (insertCapturedLocation, its only caller) but always a COORDLESS one, and
      // every searchLocations predicate requires `lat IS NOT NULL` — so a
      // promoted off-gazetteer town is unpickable exactly like an added one, and
      // reports success either way. `seed.kind` is what makes the estimates
      // clause honest here: a venue / route promote must not be told that
      // confirming will admit it to the estimates.
      onDone(true, savedMessage(seed.kind, res.searchable));
    } else if (res.status === 409) {
      onDone(false, res.message, name.trim());
    } else {
      onDone(false, res.message);
    }
  };

  return (
    <div class="admin-add">
      {/* Horizontal band-column layout mirroring the list rows (T399 item 3),
          matching AddEntryForm. Accessible names unchanged. */}
      <div class="admin-add-row">
        {seed.kind === 'route' && (
          <div class="admin-add-field admin-add-field--name">
            <label class="field-label" for="promote-hub">Start from (hub)</label>
            <input
              id="promote-hub" class="login-input" type="text"
              value={hubName} onInput={(e) => setHubName((e.target as HTMLInputElement).value)}
            />
          </div>
        )}
        <div class="admin-add-field admin-add-field--name">
          <label class="field-label" for="promote-name">Name</label>
          <input
            id="promote-name" class="login-input" type="text"
            value={name} onInput={(e) => setName((e.target as HTMLInputElement).value)}
          />
        </div>
        {bands(registry).map((band) => (
          <div key={band} class="admin-add-field admin-add-field--band">
            <label class="field-label" for={`promote-price-${band}`}>Price {band}</label>
            <input
              id={`promote-price-${band}`} class="login-input" type="number" inputMode="decimal"
              aria-describedby="promote-price-hint"
              value={prices[band] ?? ''}
              onInput={(e) => {
                setPrices({ ...prices, [band]: (e.target as HTMLInputElement).value });
                setAmountWarned(false); // Q36 re-arms on edit
              }}
            />
          </div>
        ))}
        <div class="admin-add-field admin-add-field--note">
          <label class="field-label" for="promote-note">Note</label>
          <input
            id="promote-note" class="login-input" type="text"
            value={note} onInput={(e) => setNote((e.target as HTMLInputElement).value)}
          />
        </div>
        <div class="admin-add-actions">
          <button type="button" class="btn btn--secondary" onClick={() => void submit()}>Save to the price list</button>
          <button type="button" class="btn btn--quiet" onClick={onCancel}>Cancel</button>
        </div>
      </div>
      {/* ⛔⛔ T1749 M9 rework 2 (Captain's four-gate check) — THE HINT IS A FIELD
          HINT NOW, not a paragraph that happens to sit under a form. It kept the
          screen-local `.admin-add-hint` class and no `aria-describedby`, so a
          reader who landed on a band input was never told what a blank one MEANS
          — which is the one thing no label on this row states.
          ⛔ THE WIRING IS `FormField`'s, BY HAND, because neither of these two
          forms is on `FormField` and adopting it here is a layout rewrite this
          rework does not own: one `p.field-hint` with an id, referenced from
          every band input, which is exactly what `FormField` emits (`field-hint`
          + `aria-describedby`, `packages/ui/src/FormField.tsx:497-500`).
          ⚠️ BOTH CLAUSES STAY, and the euro one is measured rather than assumed:
          these band inputs are bare `login-input`, their labels read `Price 1-4`,
          and nothing on the row shows a euro. `PriceList` earns its shorter hint
          by saying it in the LABEL ("Price in euro"); `.login-input--euro` is a
          width in `app.css`, not a symbol. Give the row a euro affordance and
          this clause goes with it. */}
      <p class="field-hint" id="promote-price-hint">Prices in euro. Leave a band blank if there's no set price.</p>
    </div>
  );
}
